/* eslint-disable max-lines */
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import FormWrapper from "../../../components/ui/FormWrapper";
import InfoTip from "../../../components/ui/InfoTip";
import Input from "../../../components/ui/input/Input";
import SelectInput from "../../../components/ui/input/SelectInput";
import Select from "../../../components/ui/select/Select";
import useFetch from "../../../hooks/useFetch";
import {
  IGTFSStation,
  IGTFSTripStop,
  IGTFSTripStopForm,
  IGTFSTripStopUpsert
} from "../../../interfaces/endpoints/IGTFS.types";
import { ITripType } from "../../../interfaces/endpoints/trips/Trips.types";
import { getGTFSStations } from "../../../services/GTFS.service";
import { getAllLines } from "../../../services/Lines.service";
import { getTripsByLine } from "../../../services/Trips.service";
import {
  formatArrivalPreview,
  formatDurationInput,
  minutesToTimeString,
  parseDurationInput,
  timeStringToMinutes,
  tripTimesLabel
} from "../../../utils/tripDuration";

const STOP_TIP =
  "Ndalesa publikohet vetëm në GTFS feed për Google, që asnjë pjesë e udhëtimit të mos kalojë 24 orë. " +
  "Është ndalesë pushimi: nuk shfaqet në faqe, nuk hipet dhe nuk zbritet. " +
  "Koha nga nisja është sa kohë pas nisjes së udhëtimit autobusi arrin te ndalesa.";

const schema = yup.object().shape({
  lineId: yup.mixed().test("line", "Linja është e detyrueshme", (v) => !!v),
  tripId: yup.mixed().test("trip", "Orari është i detyrueshëm", (v) => !!v),
  station: yup
    .object()
    .nullable()
    .test("station", "Stacioni është i detyrueshëm", (v) => !!(v && (v as { id?: number }).id)),
  offset: yup
    .string()
    .required("Koha nga nisja është e detyrueshme")
    // Hours may pass 24 for a stop on a later day, so "26:30" is allowed.
    .test("offset", "Shkruani orë:minuta, p.sh. 17:00", (v) => parseDurationInput(v ?? "") !== null)
});

// A trip's length as the backend reads it: the stored minutes, or the clocks when that is 0 (unknown).
const lengthOf = (trip: ITripType["trip"]): number => {
  if (trip.durationMinutes) return trip.durationMinutes;
  const departure = timeStringToMinutes(trip.departureTimeString ?? "");
  const arrival = timeStringToMinutes(trip.arrivalTimeString ?? "");
  return isNaN(departure) || isNaN(arrival) ? 0 : (arrival - departure + 1440) % 1440;
};

interface TripStopFormProps {
  title: string;
  initial?: IGTFSTripStop;
  // Resolves true when the row was saved; false when the backend refused it or the request failed.
  onSubmit: (payload: IGTFSTripStopUpsert) => Promise<boolean>;
}

const TripStopForm: React.FC<TripStopFormProps> = ({ title, initial, onSubmit }) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    watch,
    formState: { errors, dirtyFields }
  } = useForm<IGTFSTripStopForm>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      lineId: initial?.lineId ?? "",
      tripId: initial?.tripId ?? "",
      station: initial ? { id: initial.stationId, name: initial.stationName ?? "" } : null,
      offset: initial ? formatDurationInput(initial.arrivalMinutesAfterDeparture) : ""
    }
  });

  const { response: linesResponse } = useFetch(getAllLines, {
    initRequest: true,
    params: { pageindex: 1, pageSize: 999999 }
  });
  const {
    response: tripsResponse,
    handleFunc: loadTrips,
    setResponse: setTripsResponse
  } = useFetch(getTripsByLine, { initRequest: false, resetResponseOnError: true });

  const lineId = Number(watch("lineId")) || undefined;
  const tripId = Number(watch("tripId")) || undefined;

  useEffect(() => {
    // Drop the previous line's trips first, so a stale list can never be picked from.
    setTripsResponse(null);
    if (lineId) void loadTrips(lineId);
  }, [lineId]);

  const lineOptions = useMemo(
    () =>
      (linesResponse?.data?.result ?? []).map((line: { id: number; lineName: string }) => ({
        id: line.id,
        name: line.lineName
      })),
    [linesResponse]
  );

  const trips: ITripType[] = tripsResponse?.data ?? [];
  const tripOptions = useMemo(
    () =>
      trips.map((item) => ({
        id: item.trip.id,
        name: tripTimesLabel(
          item.trip.departureTimeString,
          item.trip.arrivalTimeString,
          item.trip.durationMinutes
        )
      })),
    [tripsResponse]
  );

  // The trip the stop belongs to: the chosen one, or the saved row's until its trips have loaded.
  const selectedTrip = trips.find((item) => item.trip.id === tripId)?.trip;
  const savedTrip = initial && initial.tripId === tripId ? initial : null;
  const tripDeparture = selectedTrip?.departureTimeString ?? savedTrip?.tripDepartureTime ?? undefined;
  const tripLength = selectedTrip ? lengthOf(selectedTrip) : (savedTrip?.tripLengthMinutes || 0);
  // The saved row's trip as a picker option, for when the line's trips no longer list it.
  const savedTripOption =
    savedTrip && tripDeparture
      ? {
          id: savedTrip.tripId,
          name:
            tripLength > 0
              ? tripTimesLabel(
                  tripDeparture,
                  minutesToTimeString(timeStringToMinutes(tripDeparture) + tripLength),
                  tripLength
                )
              : tripDeparture
        }
      : undefined;

  const offset = watch("offset");
  const minutes = parseDurationInput(offset ?? "");
  // Empty for an offset of 0, which submit refuses anyway.
  const stopArrival = formatArrivalPreview(tripDeparture, minutes ?? undefined) || null;
  const tripArrival = formatArrivalPreview(tripDeparture, tripLength) || null;
  const afterTripArrival = minutes !== null && tripLength > 0 && minutes >= tripLength;

  const toggle = (name: string) => setOpenDropdown((open) => (open === name ? null : name));

  const submit = async (data: IGTFSTripStopForm) => {
    if (!tripDeparture) {
      setError("tripId", { message: "Zgjidhni orarin" });
      return;
    }
    const arrival = parseDurationInput(data.offset) ?? 0;
    if (arrival <= 0) {
      setError("offset", { message: "Ndalesa duhet të jetë pas nisjes" });
      return;
    }

    setSubmitting(true);
    try {
      // The fields the screen does not show are carried over from the saved row, so changing the
      // time of a stop that came in through the MCP keeps its wait, confirmation and note.
      const wait = initial
        ? Math.max(0, initial.departureMinutesAfterDeparture - initial.arrivalMinutesAfterDeparture)
        : 0;
      const ok = await onSubmit({
        tripId: Number(data.tripId),
        stationId: Number(data.station?.id),
        arrivalMinutesAfterDeparture: arrival,
        departureMinutesAfterDeparture: initial ? arrival + wait : null,
        isConfirmed: initial?.isConfirmed ?? false,
        source: initial?.source ?? null
      });
      if (ok) setSaved(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormWrapper
      title={title}
      onSubmit={handleSubmit(submit)}
      // Not RHF's isSubmitSuccessful: a 400 from the backend ends the submit normally, and the
      // prompt must stay while the row is unsaved.
      showPrompt={!saved && !!Object.keys(dirtyFields).length}
      submitDisabled={submitting}
      history={initial ? { entityName: "GTFSTripStop", entityId: initial.id } : undefined}
    >
      <div className="grid gap-6">
        <SelectInput
          label="Linja*"
          placeholder="Zgjedh linjën"
          options={lineOptions}
          selectedId={lineId}
          isOpen={openDropdown === "line"}
          onClick={() => toggle("line")}
          onOptionSelect={(option) => {
            // Cleared with Backspace or Delete: option is null.
            setValue("lineId", option ? Number(option.id) : "", { shouldValidate: true });
            setValue("tripId", "", { shouldValidate: false });
            setOpenDropdown(null);
          }}
          error={errors.lineId?.message as string}
          {...register("lineId")}
        />
        <SelectInput
          label="Orari*"
          placeholder={lineId ? "Zgjedh orarin" : "Zgjidhni linjën më parë"}
          options={tripOptions}
          selectedId={tripId}
          selectedItem={savedTripOption}
          isOpen={openDropdown === "trip"}
          onClick={() => toggle("trip")}
          onOptionSelect={(option) => {
            setValue("tripId", option ? Number(option.id) : "", { shouldValidate: true });
            setOpenDropdown(null);
          }}
          error={errors.tripId?.message as string}
          {...register("tripId")}
        />
        <Controller
          control={control}
          name="station"
          render={({ field: { onChange, value } }) => (
            <Select
              label="Stacioni i ndalesës*"
              placeholder="Kërko stacionin GTFS"
              selectedItem={value as IGTFSStation}
              asyncFunc={getGTFSStations}
              onSelectedItemChange={(item) => onChange(item)}
              getOptionLabel={(o) => <span>{o.name}</span>}
              itemToString={(o) => o?.name || ""}
              error={errors.station?.message as string}
            />
          )}
        />
        <div className="grid gap-6 md:grid-cols-2">
          <Input
            label="Koha nga nisja (orë:minuta)*"
            placeholder="17:00"
            error={errors.offset?.message}
            {...register("offset")}
          />
          <div className="flex flex-col justify-end gap-1 text-sm">
            {tripDeparture ? (
              <>
                <span>
                  Udhëtimi niset {tripDeparture}
                  {tripArrival ? `, arrin ${tripArrival}` : ""}
                </span>
                <span className="flex items-center gap-1.5">
                  {stopArrival
                    ? `Ndalesa: mbërrin ${stopArrival}`
                    : "Shkruani kohën nga nisja për të parë mbërritjen"}
                  <InfoTip text={STOP_TIP} />
                </span>
                {afterTripArrival && (
                  <span className="text-red-600">
                    Ndalesa bie pas mbërritjes së udhëtimit; feed-i nuk do ta publikojë.
                  </span>
                )}
              </>
            ) : (
              <span className="flex items-center gap-1.5">
                Zgjidhni orarin për të parë nisjen dhe mbërritjen
                <InfoTip text={STOP_TIP} />
              </span>
            )}
          </div>
        </div>
      </div>
    </FormWrapper>
  );
};

export default TripStopForm;
